import {useState, useCallback, useRef} from 'react';
import constants from './constants';
import Swal from 'sweetalert2';
import authApi from './auth.api';
import {navigateTo, redirectTo} from './navigationService';
import {data} from "autoprefixer";

const useApi = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const abortControllerRef = useRef(null);

    const baseUrl = constants.API_URL;

    // Error and Success Hanlders
    const formError = async (error) => {
        await Swal.fire({
                            title: "Error",
                            text: error.message || "An error occurred while saving the form",
                            icon: "error",
                            confirmButtonColor: "#dc3545"
                        });
        throw error;
    }

    const alertResponse = async (response) => {
        if (response.status === 'success') {
            await Swal.fire({
                                title: 'Success!',
                                text: response.message,
                                icon: 'success',
                                confirmButtonText: 'Continue',
                                confirmButtonColor: '#3085d6'
                            })

            return response;
        }
    }

    const formSuccess = (response) => {
        if (response.status === 'success') {
            return response;
        } else {
            throw new Error(response.message || 'Form submission failed');
        }
    }

    // API Helpers
    // API Helpers
    const handleError = useCallback((error) => {
        if (error.name === 'AbortError') {
            console.log('Request was cancelled');
        } else {
            console.error(error);
            setError(error.message);
        }
    }, []);

    const handleUnauthorized = useCallback(() => {
        localStorage.removeItem('user');
        //sessionStorage.removeItem('user');

        const currentPath = window.location.pathname;
        if (currentPath !== '/login') {
            localStorage.setItem('redirectPath', currentPath);
        }

        navigateTo('/login', {
            from: currentPath,
            unauthorized: true
        });
    }, []);

    const fetchData = useCallback(async (url, options) => {
        setIsLoading(true);
        setError(null);

        // Cancel any ongoing requests
        if (abortControllerRef.current) {
            //abortControllerRef.current.abort();
        }

        // Create a new AbortController for this request
        abortControllerRef.current = new AbortController();
        options.signal = abortControllerRef.current.signal;

        try {
            const response = await fetch(url, options);

            // Handle 401 Unauthorized
            if (response.status === 401) {
                return handleUnauthorized();
                //throw new Error('Unauthorized - Please login');
            }
            const data = await response.json();

            if (!response.ok) {

                if (data.errors) {
                    console.log(data.errors);
                    let messages = "";
                    Object.entries(data.errors).forEach(([key, errorData]) => {
                        errorData.forEach(message => {
                            console.log(message);
                            messages += " " + message;
                        });
                    });
                    throw new Error(messages);

                } else if (data.message) {
                    throw new Error(data.message);

                } else {
                    throw new Error(`HTTP error! status: ${response.status}`);

                }
            }

            return data;
        }
        catch (error) {
            handleError(error);
            throw error;
        }
        finally {
            setIsLoading(false);
        }
    }, [handleError, handleUnauthorized]);

    const getHeaders = useCallback(() => {
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

        return {
            ...authApi.authHeader(),
            'Accept': 'application/json',
            'X-User-Timezone': timezone  // Add timezone to every request
        };
    }, []);

    const get = useCallback((endpoint) => {
        const url = `${baseUrl}${endpoint}`;
        return fetchData(url, {
            method: 'GET',
            headers: getHeaders()
        });
    }, [baseUrl, fetchData, getHeaders]);

    const post = useCallback((endpoint, data) => {
        const url = `${baseUrl}${endpoint}`;
        let body;
        let postHeaders = getHeaders();

        if (data instanceof FormData) {
            body = data;
            // Important: Remove Content-Type header when sending FormData
            delete postHeaders['Content-Type'];
        } else {
            body = JSON.stringify(data);
            postHeaders['Content-Type'] = 'application/json';
        }

        return fetchData(url, {
            method: 'POST',
            headers: postHeaders,
            body
        });
    }, [baseUrl, fetchData, getHeaders]);

    const put = useCallback((endpoint, data) => {
        const url = `${baseUrl}${endpoint}`;
        let body;
        let putHeaders = getHeaders();

        if (data instanceof FormData) {
            body = data;
            body.append('_method', 'PUT');
            // Remove Content-Type header if it exists
            delete putHeaders['Content-Type'];
        } else {
            body = JSON.stringify(data);
            putHeaders['Content-Type'] = 'application/json';
        }

        return fetchData(url, {
            method: 'PUT', // We use POST and rely on _method for Laravel
            headers: putHeaders,
            body
        });
    }, [baseUrl, fetchData, getHeaders]);

    const deleteMethod = useCallback((endpoint) => {
        const url = `${baseUrl}${endpoint}`;
        return fetchData(url, {
            method: 'DELETE',
            headers: getHeaders()
        });
    }, [baseUrl, fetchData, getHeaders]);

    // Message API Methods
    const searchThreads = useCallback((term) => {
        const endpoint = `search-messages/?term=${term}`;

        return get(endpoint);
    }, [get]);
    const fetchThreads = useCallback((userId, page) => {
        const endpoint = `messages${userId ? `/user/${userId}?page=${page}` : `?page=${page}`}`;
        return get(endpoint);
    }, [get]);

    const fetchThread = useCallback((id, page) => {
        return get(`messages/${id}?page=${page}`);
    }, [get]);

    const sendMessage = useCallback((threadId, message) => {
        return put(`messages/${threadId}`, { message });
    }, [put]);

    const createThread = useCallback((data) => {
        return post('messages', data);
    }, [post]);

    const markAsRead = useCallback((threadId) => {
        return get(`messages/${threadId}/read`);
    }, [get]);

    // Payment Routes
    const createPaymentLink = useCallback((data) => {
        return post('create-payment', data);
    }, [post]);


    // Routes
    const archiveClient = useCallback(async (id, data) => {
        return post(`archive-client/${id}`, data);
    }, [post]);

    const deleteClient = useCallback(async (id, data) => {
        return post(`delete-client/${id}`, data);
    }, [post]);

    const unarchiveClient = useCallback(async (id, data) => {
        return post(`unarchive-client/${id}`, data);
    }, [post]);

    const approvePortalRequest = useCallback(async (id) => {
        return post(`portal-request/${id}`);
    }, [post]);

    const denyPortalRequest = useCallback(async (id) => {
        return deleteMethod(`portal-request/${id}`);
    }, [deleteMethod]);

    const getCount = useCallback(() => {
        return get('count');
    }, [get]);

    const getClients = useCallback(async (query) => {
        Object.keys(query).forEach(key => {
            if (query[key] === null || query[key] === undefined || query[key] === '') {
                delete query[key];
            }
        });
        const params = new URLSearchParams(query);
        return get(`clients?${params}`);
    }, [get]);

    const searchClients = useCallback(async (query) => {
        const params = new URLSearchParams(query);
        return get(`search-clients?${params}`);
    }, [get])

    const getReport = useCallback(async (page = 1, filters, searchTerm = '', download = false) => {
        let params;
        if (filters) {
            let data = {
                page,
                term: searchTerm,
            }

            Object.entries(filters).forEach(([key, value]) => {
                data[key] = value;

                if (value === null || value === undefined || value === '') {
                    delete data[key];
                }
            });

            params = new URLSearchParams(data);
        } else {
            params = new URLSearchParams({
                                             page,
                                             ...(searchTerm && { term: searchTerm })
                                         });
        }

        if (download) {
            setIsLoading(true);
            setError(null);

            try {
                const headers = getHeaders();
                headers['Accept'] = 'text/csv';

                const response = await fetch(`${baseUrl}report?${params}`, {
                    method: 'GET',
                    headers: headers,
                });

                if (!response.ok) {
                    throw new Error('Download failed');
                }

                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = `tax_services_report_${new Date().toISOString().split('T')[0]}.csv`;
                document.body.appendChild(link);
                link.click();

                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);

            }
            catch (error) {
                handleError(error);
                throw error;
            }
            finally {
                setIsLoading(false);
            }
            return;
        }

        return get(`report?${params}`);
    }, [get, baseUrl, getHeaders, handleError]);

    const getClient = useCallback((user_id, data) => {
        const params = new URLSearchParams(data);

        return get(`client/${user_id}?${params}`);
    }, [get]);

    const getClientEdit = useCallback((user_id, data) => {
        const params = new URLSearchParams(data);

        return get(`client/${user_id}/edit?${params}`);
    }, [get]);

    const getClientHome = useCallback((uid, data) => {
        const params = new URLSearchParams(data);

        if (uid) {
            return get(`client-home/${uid}?${params}`);
        } else {
            return get(`client-home?${params}`);
        }
    }, [get]);

    const getDocumentStatus = useCallback((userId, data) => {

        if (data) {
            const params = new URLSearchParams(data);

            return get(`document-status/${userId}?${params}`);
        }

        return get(`document-status/${userId}`);
    }, [get]);

    const getDocuments = useCallback((data) => {
        const params = new URLSearchParams(data);

        return get(`documents?${params}`);
    }, [get]);

    const getClientIdentification = useCallback((user_id) => {
        return get(`client-identification/${user_id}`);
    }, [get]);

    const getClientDocuments = useCallback((user_id, data) => {
        const params = new URLSearchParams(data);

        return get(`documents/${user_id}?${params}`);
    }, [get]);

    const getAdminDashboard = useCallback((data) => {
        const params = new URLSearchParams(data);

        return get(`dashboard?${params}`);
    }, [get]);

    const getAdminClientDashboard = useCallback((uid, data) => {
        const params = new URLSearchParams(data);

        return get(`client-dashboard/${uid}?${params}`);
    }, [get]);

    const getForms = useCallback(() => {
        return get(`forms`);
    }, [get]);

    const getFormBuilder = useCallback((id) => {
        if (id) {
            return get(`form-builder/${id}`);
        }

        return get(`form-builder`);

    }, [get]);

    const getPreparers = useCallback(() => {
        return get(`preparers`);
    }, [get]);

    const duplicateForm = useCallback((id, data) => {
        return post(`duplicate-form/${id}`, data);
    }, [post]);

    const updateFormBuilder = useCallback((id, data) => {
        return post(`form-builder/${id}`, data);
    }, [post]);

    const deleteFormBuilder = useCallback((id, data) => {
        return deleteMethod(`form-builder/${id}`);
    }, [deleteMethod]);

    const getProfile = useCallback((data) => {
        const params = new URLSearchParams(data);

        return get(`profile?${params}`);
    }, [get]);

    const getPortalRequests = useCallback(() => {
        return get(`portal-requests`);
    }, [get]);

    const getSentLetters = useCallback((uid) => {
        return get(`sent-letters/${uid}`);
    }, [get]);

    const getEmailTemplate = useCallback((template_id, uid) => {
        return get(`email-template/${template_id}/${uid}`);
    }, [get]);

    const getEmailTemplates = useCallback((uid) => {
        return get(`email-templates/${uid}`);
    }, [get]);

    const getTaxQuestionnaire = useCallback((data) => {
        const params = new URLSearchParams(data);
        return get(`tax-questionnaire?${params}`);
    }, [get]);

    const getClientTaxQuestionnaire = useCallback((uid, print, data) => {
        const params = new URLSearchParams(data);

        if (print) {
            return get(`tax-questionnaire/${uid}/${print}?${params}`);
        }

        return get(`tax-questionnaire/${uid}?${params}`);
    }, [get]);

    const deleteDocument = useCallback(async (id, userId, fileId) => {
        try {
            const result = await Swal.fire({
                                               title: "Are you sure?",
                                               text: "Selected Document will be deleted!",
                                               icon: "warning",
                                               showCancelButton: true,
                                               confirmButtonColor: "#dc3545",
                                               cancelButtonColor: "#6c757d",
                                               confirmButtonText: "Yes, Go Ahead",
                                               cancelButtonText: "No, cancel!",
                                               reverseButtons: true
                                           });

            if (result.isConfirmed) {
                const response = await post('delete/document', {
                    id: id,
                    us: userId,
                    f: fileId
                });

                if (response.status === 'done') {
                    await Swal.fire({
                                        title: "Deleted!",
                                        text: "Document Deleted",
                                        icon: "success",
                                        confirmButtonColor: "#198754"
                                    });
                    return true;
                } else {
                    throw new Error(response.message || 'Delete operation failed');
                }
            } else {
                await Swal.fire({
                                    title: "Cancelled",
                                    text: "Selection canceled :)",
                                    icon: "error",
                                    confirmButtonColor: "#dc3545"
                                });
                return false;
            }
        }
        catch (error) {
            await Swal.fire({
                                title: "Error",
                                text: error.message || "An error occurred while deleting the document",
                                icon: "error",
                                confirmButtonColor: "#dc3545"
                            });
            return false;
        }
    }, [post]);

    const downloadDocument = useCallback(async (doc, file) => {
        setIsLoading(true);
        setError(null);

        try {
            const headers = getHeaders();
            const response = await fetch(`${baseUrl}download/${doc.id}/${file.id}`, {
                method: 'GET',
                headers: headers,
            });

            if (!response.ok) {
                throw new Error('Download failed');
            }

            // Get the blob from the response
            const blob = await response.blob();

            // Create a URL for the blob
            const url = window.URL.createObjectURL(blob);

            // Create a temporary link and trigger download
            const link = document.createElement('a');
            link.href = url;
            link.download = file.file_name || 'download';
            document.body.appendChild(link);
            link.click();

            // Cleanup
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

        }
        catch (error) {
            handleError(error);
            throw error;
        }
        finally {
            setIsLoading(false);
        }
    }, [baseUrl, getHeaders, handleError]);

    const downloadZip = useCallback(async (data) => {
        setIsLoading(true);
        setError(null);

        try {
            const params = new URLSearchParams(data);
            const headers = getHeaders();

            const response = await fetch(`${baseUrl}download-zip?${params}`, {
                method: 'GET',
                headers: headers,
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Download failed');
            }

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');

            // Extract filename from Content-Disposition
            const contentDisposition = response.headers.get('Content-Disposition');
            let filename = 'documents.zip';

            if (contentDisposition) {
                const matches = contentDisposition.match(/filename="(.+)"/);
                if (matches && matches[1]) {
                    filename = matches[1];
                }
            }

            link.href = url;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

        } catch (error) {
            console.error('Download error:', error);
            setError(error.message || 'Failed to download zip file');
            throw error;
        } finally {
            setIsLoading(false);
        }
    }, [baseUrl, getHeaders]);

    const printClient = useCallback(async (id) => {
        return get(`client/print/${id}`);
    }, [get]);

    const readyToFile = useCallback(async (id, data) => {
        return post(`ready-to-file/${id}`, data);
    }, [post]);

    const saveForm = useCallback(async (uid, data) => {
        try {
            const response = await post(`tax-questionnaire/${uid}`, data);
            return formSuccess(response);
        }
        catch (error) {
            return await formError(error);
        }
    }, [post]);

    const savePortalRequest = useCallback(async (data) => {
        try {
            const response = await post('portal-request/save', data);
            if (response.status === 'success') {
                return response;
            } else {
                throw new Error(response.message || 'Form submission failed');
            }
        }
        catch (error) {
            console.log(error);
            await Swal.fire({
                                title: "Error",
                                text: error.message || "An error occurred while saving the form",
                                icon: "error",
                                confirmButtonColor: "#dc3545"
                            });
            throw error;
        }
    }, [post]);

    const submitLetter = useCallback(async (data) => {
        try {
            const response = await post('submit-letter', data);
            if (response.status === 'success') {
                return response;
            } else {
                throw new Error(response.message || 'Form submission failed');
            }
        }
        catch (error) {
            await Swal.fire({
                                title: "Error",
                                text: error.message || "An error occurred while sending the letter",
                                icon: "error",
                                confirmButtonColor: "#dc3545"
                            });
            throw error;
        }
    }, [post]);

    const uploadDocument = useCallback((data) => {
        return post('upload/document', data);
    }, [post]);

    const deleteId = useCallback(async (uid, document) => {
        try {
            const response = await post('delete-id', {
                id: document.document_id,
                uid: uid
            });

            if (!response.success) {
                throw new Error(response.message || 'Failed to delete ID document');
            }

            return response;
        }
        catch (error) {
            console.error('Error deleting ID document:', error);
            throw error;
        }
    }, [post]);

    const uploadId = useCallback((formData) => {
        return post('upload-id', formData);
    }, [post]);

    const updateProfile = useCallback((id, data) => {
        return post(`profile/${id}/update`, data);
    }, [post]);

    const createClient = useCallback((data) => {
        return put(`client/save`, data);
    }, [put]);

    const updateProfileFlag = useCallback(async (id, data) => {
        const response = await post(`update-profile-flag`, data);

        return alertResponse(response);
    }, [post]);

    const updateProfileStatus = useCallback(async (id, data) => {
        return post(`update-profile-status/${id}`, data);
    }, [post]);

    const setClientPassword = useCallback(async (uid, data) => {
        return post(`set-client-password/${uid}`, data);
    }, [post]);

    return {
        isLoading,
        error,
        // Message methods
        fetchThreads,
        fetchThread,
        sendMessage,
        createThread,
        markAsRead,
        searchThreads,
        // Routes
        approvePortalRequest,
        denyPortalRequest,
        archiveClient,
        createClient,
        createPaymentLink,
        deleteClient,
        deleteId,
        duplicateForm,
        deleteFormBuilder,
        updateFormBuilder,
        unarchiveClient,
        deleteDocument,
        downloadDocument,
        downloadZip,
        getAdminDashboard,
        getAdminClientDashboard,
        getClient,
        getClientEdit,
        getClientIdentification,
        getClientDocuments,
        getClientHome,
        getClientTaxQuestionnaire,
        getClients,
        getCount,
        getDocuments,
        getDocumentStatus,
        getForms,
        getFormBuilder,
        getPortalRequests,
        getPreparers,
        getProfile,
        getSentLetters,
        getEmailTemplate,
        getEmailTemplates,
        getReport,
        getTaxQuestionnaire,
        printClient,
        readyToFile,
        saveForm, // Save Tax Questionnaire
        searchClients,
        setClientPassword,
        submitLetter,
        savePortalRequest,
        updateProfile,
        updateProfileFlag,
        updateProfileStatus,
        uploadDocument,
        uploadId,
    };

}

export default useApi;
